import { ERROR_CODE, SENSOR_TYPE, SensorKeySchema, UniversalError, parse } from "@packages/core";
import { useForm, useStore } from "@tanstack/react-form";
import { Link, createFileRoute, useBlocker, useRouter } from "@tanstack/react-router";
import { useMemo } from "react";
import * as v from "valibot";

import {
  ButtonStyle,
  FormField,
  FormFieldInput,
  FormFieldInputStyle,
  PageName,
  PaperStyle,
  TypographyStyle,
  showError,
} from "#components";
import {
  $alert,
  $confirm,
  modifySensor,
  updateSensorQueryData,
  useFacilityQuery,
  useGetErrorMessage,
  useSensor,
  useTranslate,
} from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/facilities/$facilityKey/sensors/$sensorKey")({
  component: Component,
  params: {
    parse(parameters) {
      return v.parse(v.object({ sensorKey: SensorKeySchema() }), parameters);
    },
  },
});

function Component() {
  const { facilityKey, sensorKey } = Route.useParams();

  const { data: facility } = useFacilityQuery(facilityKey);
  const getErrorMessage = useGetErrorMessage();
  const router = useRouter();
  const sensor = useSensor(sensorKey);
  const translate = useTranslate();

  const schema = useMemo(function () {
    return v.object({
      name: v.pipe(
        v.string(),
        v.trim(),
        v.minLength(1, ERROR_CODE.INPUT_REQUIRED),
        v.maxGraphemes(48, ERROR_CODE.TOO_MANY_CHARACTERS),
      ),
    });
  }, []);
  const defaultValues = useMemo(
    function (): v.InferInput<typeof schema> {
      return { name: sensor?.name ?? "" };
    },
    [sensor],
  );

  const form = useForm({
    defaultValues,
    async onSubmit({ value: input }) {
      if (!sensor) {
        showError(new UniversalError(ERROR_CODE.SENSOR_NOT_FOUND));
        return;
      }

      try {
        const output = parse(schema, input);
        console.debug("[onSubmit]", { output, input }, new Date());

        const result = await modifySensor({ properties: output, sensorKey });

        await $alert({
          color: "success",
          content: translate("%Register.2%", { $: { name: result.sensor.name } }),
          title: translate("%Register.0%"),
        });

        updateSensorQueryData(result.sensor.sensorKey, result.sensor);

        if (router.history.canGoBack()) {
          router.history.back();
        } else {
          await router.navigate({
            ignoreBlocker: true,
            params: { facilityKey },
            to: "/facilities/$facilityKey/sensors",
          });
        }
      } catch (error) {
        showError(error);
      }
    },
    onSubmitInvalid({ meta, value: input }) {
      console.warn("無効な入力", input, meta);
    },

    validators: { onSubmit: schema },
  });

  {
    const isDefaultValue = useStore(form.store, (state) => state.isDefaultValue);
    const isSubmitted = useStore(form.store, (state) => state.isSubmitted);
    const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
    useBlocker({
      async shouldBlockFn() {
        return (!isDefaultValue && !isSubmitted) || isSubmitting
          ? !(await $confirm({
              color: "warning",
              content: translate("%Form.1%"),
              no: translate("%Stay.0%"),
              title: translate("%Form.0%"),
              yes: translate("%Leave.0%"),
            }))
          : false;
      },
      withResolver: true,
    });
  }

  return (
    <main
      className={css({
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        padding: { smDown: "16px", sm: "24px 32px" },
      })}
    >
      <PageName>
        {facility && sensor ? sensor.name + " - " + facility.name : translate("%Loading.0%")}
      </PageName>

      <form
        className={css(PaperStyle.raw({ borderRadius: "12px", padding: "24px" }), {
          maxWidth: "480px",
          width: "stretch",
        })}
        onSubmit={function (event) {
          event.preventDefault();
          form.handleSubmit();
        }}
      >
        <h1 className={css({ fontSize: "18px", fontWeight: 600 })}>
          {translate("%SensorInformation.0%")}
        </h1>

        <div
          className={css({
            columnGap: "12px",
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            rowGap: "16px",
            marginTop: "16px",
          })}
        >
          <FormField className={css({ width: "100%" })} label={translate("%SensorKey.0%")}>
            <FormFieldInput
              className={css(
                TypographyStyle.raw({ monospace: true }),
                FormFieldInputStyle.raw({ width: "100%" }),
              )}
              disabled
              value={sensor?.sensorKey ?? ""}
            />
          </FormField>

          {sensor && (
            <FormField className={css({ width: "100%" })} label={translate("%SensorType.0%")}>
              <FormFieldInput
                className={css(
                  TypographyStyle.raw({ monospace: true }),
                  FormFieldInputStyle.raw({ width: "100%" }),
                )}
                disabled
                value={translate("%SensorType." + sensor.type + ".0%")}
              />
            </FormField>
          )}
        </div>

        {sensor?.type === SENSOR_TYPE.AAMS && (
          <FormField
            className={css({ marginTop: "16px", width: "100%" })}
            label={translate("%MacAddress.0%")}
          >
            <FormFieldInput
              className={css(
                TypographyStyle.raw({ monospace: true }),
                FormFieldInputStyle.raw({ width: "100%" }),
              )}
              disabled
              value={sensor.macAddress}
            />
          </FormField>
        )}

        {sensor?.type === SENSOR_TYPE.ANSIEL && (
          <FormField
            className={css({ marginTop: "16px", width: "100%" })}
            label={translate("%SerialNumber.0%")}
          >
            <FormFieldInput
              className={css(
                TypographyStyle.raw({ monospace: true }),
                FormFieldInputStyle.raw({ width: "100%" }),
              )}
              disabled
              value={sensor.serialNumber}
            />
          </FormField>
        )}

        <form.Field name="name" validators={{ onChange: schema.entries.name }}>
          {function (field) {
            return (
              <FormField
                caution={field.state.meta.errors
                  .map(function (error) {
                    return getErrorMessage(error);
                  })
                  .join("")}
                className={css({ marginTop: "16px" })}
                invalid={field.state.meta.errors.length > 0}
                label={translate("%SensorName.0%")}
                required
              >
                <FormFieldInput
                  className={FormFieldInputStyle({ width: "100%" })}
                  id={field.name}
                  onBlur={() => field.handleBlur()}
                  onChange={function (event) {
                    field.handleChange(event.target.value);
                  }}
                  value={field.state.value}
                />
              </FormField>
            );
          }}
        </form.Field>

        <div
          className={css({
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "8px",
            marginTop: "16px",
          })}
        >
          <Link
            className={css(
              ButtonStyle.raw({
                appearance: "outlined",
                borderRadius: "4px",
                color: "primary",
                padding: "10px 16px",
              }),
              { width: "100%" },
            )}
            onClick={function (event) {
              if (router.history.canGoBack()) {
                event.preventDefault();
                router.history.back();
              }
            }}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/sensors"
          >
            {translate("%Cancel.0%")}
          </Link>

          <form.Subscribe
            selector={function (state) {
              return state.isSubmitting;
            }}
          >
            {function (submitting) {
              return (
                <button
                  className={css(
                    ButtonStyle.raw({
                      appearance: "contained",
                      borderRadius: "4px",
                      color: "primary",
                      padding: "10px 16px",
                    }),
                    {
                      width: "100%",
                    },
                  )}
                  disabled={submitting}
                  type="submit"
                >
                  {translate("%Save.0%")}
                </button>
              );
            }}
          </form.Subscribe>
        </div>
      </form>
    </main>
  );
}
